/**
 * Options: the chain, structures built from it, and the volatility premium.
 *
 * The panel is arranged so the two numbers that must be read together are
 * always adjacent: probability of profit (what the market charges) and
 * expected value (what it is worth under those same prices). Showing a win
 * rate alone would let a 76%-probability structure with a 5:1 loss ratio look
 * like a good trade.
 */
import {
  type ChainSummary, type Expiry, type OptionContract, type Structure,
  type VrpResult,
  LIQUIDITY_TONE, formatNum, formatPct, formatVolume,
} from "@tt/shared";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { OptionDetailModal } from "../components/OptionDetailModal";
import { PayoffChart } from "../components/PayoffChart";
import { useAsync, usePersisted } from "../hooks/useApi";
import { api } from "../lib/api";

type DetailTarget = {
  contract: OptionContract;
  kind: "call" | "put";
};

type Tab = "chain" | "structures" | "volatility";

export function OptionsPanel({ symbol }: { symbol: string }) {
  const [tab, setTab] = useState<Tab>("chain");
  const [expiry, setExpiry] = useState<string | null>(null);
  // Both persisted: these are a reading preference, not transient state, and
  // resetting them on every reload meant choosing the layout again each time
  // the page came back.
  const [layout, setLayout] = usePersisted<"split" | "list">("tt.opt.layout", "split");
  const [view, setView] = usePersisted("tt.opt.side", "all");
  const [side, setSide] = useState<"both" | "calls" | "puts">("both");
  // Selected contract for the Simulated Returns modal. Null means closed.
  const [detail, setDetail] = useState<DetailTarget | null>(null);

  const expiries = useAsync((s) => api.expiries(symbol, s), [symbol]);
  const chain = useAsync((s) => api.chain(symbol, expiry, s), [symbol, expiry]);
  const suggest = useAsync(
    (s) => api.optionSuggest(symbol, expiry, s),
    [symbol, expiry],
    tab === "structures",
  );
  const vrp = useAsync((s) => api.vrp(symbol, 30, s), [symbol], tab === "volatility");

  const summary = chain.data?.summary;

  return (
    <div className="panel">
      <div className="row" style={{ marginBottom: 10 }}>
        <div className="tf-group">
          {(["chain", "structures", "volatility"] as const).map((t) => (
            <button
              key={t}
              className={`btn small ${tab === t ? "active" : ""}`}
              onClick={() => setTab(t)}
            >
              {t === "chain" ? "Chain" : t === "structures" ? "Structures" : "Volatility"}
            </button>
          ))}
        </div>
        {tab === "chain" && (
          <>
            <div className="tf-group">
              {(["both", "calls", "puts"] as const).map((s) => (
                <button
                  key={s}
                  className={`btn small ${side === s ? "active" : ""}`}
                  onClick={() => setSide(s)}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="tf-group">
              {(["split", "list"] as const).map((l) => (
                <button
                  key={l}
                  className={`btn small ${layout === l ? "active" : ""}`}
                  onClick={() => setLayout(l)}
                  title={
                    l === "split"
                      ? "Calls and puts either side of a shared strike column"
                      : "Calls and puts as separate tables, one per row"
                  }
                >
                  {l}
                </button>
              ))}
            </div>
          </>
        )}
        {tab === "structures" && (
          <select className="select" value={view} onChange={(e) => setView(e.target.value)}>
            {["all", "bullish", "bearish", "neutral", "volatility"].map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        )}
        <span className="spacer" />
        {chain.data && (
          <span className="small faint mono">
            {chain.data.expiry} · {chain.data.days_to_expiry}d ·{" "}
            {chain.data.calls.length + chain.data.puts.length} contracts
          </span>
        )}
      </div>

      <ExpiryStrip
        expiries={expiries.data?.expiries ?? []}
        selected={chain.data?.expiry ?? expiry}
        onPick={setExpiry}
      />

      {chain.loading && !chain.data && (
        <div className="hint"><span className="spinner" /> loading {symbol} chain…</div>
      )}
      {chain.error && (() => {
        // "no listed options for X" is a factual state, not a system
        // error -- the symbol just doesn't trade options (think most
        // small caps and recent IPOs). Render it as an empty state so
        // the user doesn't read "something broke". Everything else
        // (timeouts, 5xx, auth issues) still shows the error strip.
        const msg = chain.error.message || "";
        const isMissing = /no listed options|no options|not listed/i.test(msg);
        if (isMissing) {
          return (
            <div className="opt-empty">
              <div className="opt-empty-ico" aria-hidden>📭</div>
              <div className="opt-empty-title">No options chain for {symbol}</div>
              <div className="opt-empty-sub">
                This symbol doesn't trade listed options. Common for small
                caps, recent IPOs, OTC tickers, and ADRs. Try a liquid
                large-cap (AAPL, NVDA, SPY) if you're exploring.
              </div>
            </div>
          );
        }
        return <div className="err">{msg}</div>;
      })()}
      {chain.data?.warnings.map((w, i) => <div className="warnbox" key={i}>{w}</div>)}

      {summary && <Summary s={summary} spot={chain.data!.spot} />}

      {tab === "chain" && chain.data && (
        layout === "split" ? (
          <ChainTable
            calls={chain.data.calls}
            puts={chain.data.puts}
            spot={chain.data.spot}
            side={side}
            expiry={chain.data.expiry}
            days={chain.data.days_to_expiry}
            onPickContract={(c, k) => setDetail({ contract: c, kind: k })}
          />
        ) : (
          <ChainList
            calls={chain.data.calls}
            puts={chain.data.puts}
            spot={chain.data.spot}
            side={side}
            expiry={chain.data.expiry}
            days={chain.data.days_to_expiry}
            onPickContract={(c, k) => setDetail({ contract: c, kind: k })}
          />
        )
      )}

      {detail && chain.data && (
        <OptionDetailModal
          symbol={symbol}
          contract={detail.contract}
          kind={detail.kind}
          spot={chain.data.spot}
          expiry={chain.data.expiry}
          days={chain.data.days_to_expiry}
          onClose={() => setDetail(null)}
        />
      )}

      {tab === "structures" && (
        <Structures
          state={suggest}
          spot={chain.data?.spot ?? 0}
          expectedMove={summary?.expected_move_1sd ?? null}
          view={view}
        />
      )}

      {tab === "volatility" && <Volatility state={vrp} symbol={symbol} />}
    </div>
  );
}

/** Which side of 1.0 a ratio sits on, in words rather than in colour. */
function ratioSub(r: number | null): string | undefined {
  if (r === null) return undefined;
  if (r > 1.02) return "more puts";
  if (r < 0.98) return "more calls";
  return "balanced";
}

// ─────────────────────────── summary ───────────────────────────
function Summary({ s, spot }: { s: ChainSummary; spot: number }) {
  return (
    <>
      <div className="kv" style={{ marginBottom: 8 }}>
        <Cell k="ATM implied vol" v={formatPct(s.atm_iv)} tone="accent" />
        <Cell
          k="expected move (1σ)"
          v={s.expected_move_1sd ? `±$${formatNum(s.expected_move_1sd)}` : "—"}
          sub={s.expected_move_pct ? formatPct(s.expected_move_pct) : undefined}
          tone="accent"
        />
        {/* The two bounds take the colour of the direction they sit in. It is
            one range, so they are coloured in place rather than given a tone
            for the whole cell. */}
        <Cell k="expected range">
          {s.expected_range ? (
            <>
              <span className="down">{formatNum(s.expected_range[0])}</span>
              <span className="faint"> – </span>
              <span className="up">{formatNum(s.expected_range[1])}</span>
            </>
          ) : "—"}
        </Cell>
        <Cell
          k="ATM straddle"
          v={s.straddle_price ? `$${formatNum(s.straddle_price)}` : "—"}
          sub={s.straddle_implied_move_pct ? `${formatPct(s.straddle_implied_move_pct)} move` : undefined}
          tone="accent"
        />
        {/* No tone on either ratio, on purpose -- see Cell. */}
        <Cell
          k="put/call volume"
          v={formatNum(s.put_call_volume_ratio)}
          sub={ratioSub(s.put_call_volume_ratio)}
        />
        <Cell
          k="put/call OI"
          v={formatNum(s.put_call_oi_ratio)}
          sub={ratioSub(s.put_call_oi_ratio)}
        />
        <Cell
          k="max pain"
          v={s.max_pain ? formatNum(s.max_pain, 0) : "—"}
          sub={s.max_pain && spot ? `${s.max_pain >= spot ? "+" : ""}${(((s.max_pain - spot) / spot) * 100).toFixed(1)}% vs spot` : undefined}
          title="The strike where the most open contracts expire worthless. A property of open interest, not a forecast."
        />
        {/* The sign says which wing is bid richer, which is a fact about
            prices rather than a view on where the stock goes. */}
        <Cell
          k="25Δ skew"
          v={s.skew_25d !== null ? `${(s.skew_25d * 100).toFixed(2)} pts` : "—"}
          sub="puts minus calls"
          tone={s.skew_25d === null ? undefined : s.skew_25d >= 0 ? "down" : "up"}
          title="Positive means 25-delta puts carry more implied vol than the matching calls -- downside protection is the better bid."
        />
      </div>
      {s.unusual.length > 0 && (
        <>
          <h3>Unusual activity · volume above open interest</h3>
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th>Type</th><th>Strike</th><th>Volume</th><th>OI</th>
                  <th>Vol/OI</th><th>Mid</th><th>IV</th><th>Notional</th>
                </tr>
              </thead>
              <tbody>
                {s.unusual.slice(0, 8).map((u) => (
                  <tr key={u.symbol}>
                    <td className={u.type === "call" ? "up" : "down"}>{u.type}</td>
                    <td className="num">{formatNum(u.strike, 1)}</td>
                    <td className="num">{formatVolume(u.volume)}</td>
                    <td className="num faint">{formatVolume(u.open_interest)}</td>
                    <td className="num">{u.volume_oi_ratio}×</td>
                    <td className="num">{formatNum(u.mid)}</td>
                    <td className="num">{formatPct(u.iv, 0)}</td>
                    <td className="num">${formatVolume(u.notional)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div className="notebox">
        <ul className="tight">
          {s.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </div>
      <div className="faint small" style={{ marginBottom: 6 }}>spot {formatNum(spot)}</div>
    </>
  );
}

/**
 * One figure in the summary strip.
 *
 * `tone` is deliberately narrow. "accent" is for a magnitude -- a number with
 * a size but no side -- and up/down only for figures whose sign genuinely
 * says which way prices lean. The put/call ratios get neither, because this
 * panel's own note says they describe positioning rather than sentiment, and
 * painting them green and red would contradict that in the louder medium.
 */
function Cell({ k, v, sub, tone, title, children }: {
  k: string; v?: string; sub?: string;
  tone?: "accent" | "up" | "down";
  title?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="kv-cell" title={title}>
      <div className="k">{k}</div>
      <div className={`v ${tone ?? ""}`}>{children ?? v}</div>
      {sub && <div className="faint small">{sub}</div>}
    </div>
  );
}

// ─────────────────────────── chain ───────────────────────────
function ChainTable({
  calls, puts, spot, side, expiry, days, onPickContract,
}: {
  calls: OptionContract[]; puts: OptionContract[]; spot: number; side: string;
  expiry: string; days: number;
  onPickContract?: (c: OptionContract, kind: "call" | "put") => void;
}) {
  const strikes = useMemo(() => {
    const set = new Set<number>();
    if (side !== "puts") for (const c of calls) set.add(c.strike);
    if (side !== "calls") for (const p of puts) set.add(p.strike);
    return [...set].sort((a, b) => a - b);
  }, [calls, puts, side]);

  const callBy = useMemo(() => new Map(calls.map((c) => [c.strike, c])), [calls]);
  const putBy = useMemo(() => new Map(puts.map((p) => [p.strike, p])), [puts]);

  return (
    <>
      {/* The expiry belongs on the table, not only in the picker above it.
          Every greek, every probability and every price in these rows is a
          function of it, and it was previously off-screen the moment you
          scrolled -- so the numbers could be read without knowing which
          expiry produced them. */}
      <h3>
        Chain · <span style={{ color: "var(--text-dim)" }}>{expiry}</span> ·{" "}
        {days}d · {strikes.length} strikes
      </h3>
      <div className="table-scroll" style={{ maxHeight: "48vh", overflowY: "auto" }}>
        <table className="grid chain">
          <thead>
            <tr>
              {side !== "puts" && <th colSpan={8} className="side-call">CALLS</th>}
              <th className="strike-col">STRIKE</th>
              {side !== "calls" && <th colSpan={8} className="side-put">PUTS</th>}
            </tr>
            <tr>
              {side !== "puts" && (
                <>
                  <th>OI</th><th>Vol</th><th>IV</th><th>Δ</th><th>Θ</th>
                  <th>P(ITM)</th><th>Bid</th><th>Ask</th>
                </>
              )}
              {/* The expiry lives here rather than under STRIKE above. It has
                  to survive scrolling -- every greek and price in these rows
                  is a function of it -- but stacked in the first row it made
                  that row tall, and the second header row was the useless
                  "—". One slot now carries the only thing worth saying. */}
              <th className="strike-col expiry">{expiry} · {days}d</th>
              {side !== "calls" && (
                <>
                  <th>Ask</th><th>Bid</th><th>P(ITM)</th><th>Θ</th><th>Δ</th>
                  <th>IV</th><th>Vol</th><th>OI</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {strikes.map((k) => {
              const c = callBy.get(k);
              const p = putBy.get(k);
              const nearSpot = Math.abs(k - spot) < spot * 0.006;
              return (
                <tr key={k} style={nearSpot ? { background: "var(--accent-dim)" } : undefined}>
                  {side !== "puts" && (
                    <SideCells
                      c={c} kind="call"
                      onPick={c && onPickContract
                        ? () => onPickContract(c, "call")
                        : undefined}
                    />
                  )}
                  <td className="num mono strike-col">{formatNum(k, 1)}</td>
                  {side !== "calls" && (
                    <SideCells
                      c={p} kind="put" reverse
                      onPick={p && onPickContract
                        ? () => onPickContract(p, "put")
                        : undefined}
                    />
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="notebox">
        Δ is approximately the market's own probability that the option finishes
        in the money, and P(ITM) is that probability exactly. A strike showing
        P(ITM) of 5% is the market selling you a 95% chance of expiring
        worthless — and pricing the premium to match. Rows with a zero bid are
        marked untradeable: nobody is offering to buy them at any price.
      </div>
    </>
  );
}

function SideCells({
  c, kind, reverse = false, onPick,
}: {
  c: OptionContract | undefined; kind: string; reverse?: boolean;
  onPick?: () => void;
}) {
  if (!c) {
    return (
      <>
        {Array.from({ length: 8 }, (_, i) => <td key={i} className="dim">—</td>)}
      </>
    );
  }
  // Row-level click fires the detail modal. One click handler per cell so
  // the Reverse-order rendering doesn't lose it. Cursor comes from style
  // merging below (bid/ask cells need to preserve their liquidity-wash bg).
  const clickProps = onPick ? { onClick: onPick } : {};
  const tone = LIQUIDITY_TONE[c.liquidity] ?? "muted";
  const liquidity = `${c.liquidity}${c.notes.length ? ` — ${c.notes.join(" ")}` : ""}`;
  // Bid and ask are their own columns, coloured like the two sides of the
  // book as they are in the list view. The liquidity verdict used to be the
  // colour of the combined cell; split, it becomes a background wash across
  // both, so a thin or untradeable quote still announces itself without
  // taking the colour that now says which side you are reading.
  const washed = tone === "bad" || tone === "warn"
    ? { background: tone === "bad" ? "var(--tone-bad-bg)" : "var(--tone-warn-bg)" }
    : undefined;
  const mergeStyle = (base?: React.CSSProperties): React.CSSProperties | undefined => {
    if (!onPick) return base;
    return { ...(base ?? {}), cursor: "pointer" };
  };
  const bidCell = (
    <td className="num mono bid" key="qb" title={liquidity} style={mergeStyle(washed)} {...clickProps}>
      {formatNum(c.bid)}
    </td>
  );
  const askCell = (
    <td className="num mono ask" key="qa" title={liquidity} style={mergeStyle(washed)} {...clickProps}>
      {formatNum(c.ask)}
    </td>
  );
  // A chain is mostly dashes wherever the provider has no quote to imply vol
  // from -- deep ITM calls and far OTM puts. Dimming the empty ones is what
  // lets the strikes that do have greeks carry the eye down the column; at
  // full strength the dashes read as content and crowd the real figures.
  const gap = (v: number | null) => (v === null ? "num dim" : "num");
  const cells = [
    <td className={`${gap(c.open_interest)} faint`} key="oi" {...clickProps}>{formatVolume(c.open_interest)}</td>,
    <td className={gap(c.volume)} key="v" {...clickProps}>{formatVolume(c.volume)}</td>,
    <td className={gap(c.iv)} key="iv" {...clickProps}>{formatPct(c.iv, 0)}</td>,
    <td
      className={c.delta === null ? "num dim" : `num ${kind === "call" ? "up" : "down"}`}
      key="d"
      {...clickProps}
    >
      {formatNum(c.delta, 2)}
    </td>,
    <td className={`${gap(c.theta)} faint`} key="t" {...clickProps}>{formatNum(c.theta, 2)}</td>,
    <td className={gap(c.prob_itm)} key="p" {...clickProps}>{formatPct(c.prob_itm, 0)}</td>,
    bidCell,
    askCell,
  ];
  return <>{reverse ? [...cells].reverse() : cells}</>;
}

// ─────────────────────────── structures ───────────────────────────
function Structures({
  state, spot, expectedMove, view,
}: {
  state: ReturnType<typeof useAsync<Awaited<ReturnType<typeof api.optionSuggest>>>>;
  spot: number;
  expectedMove: number | null;
  view: string;
}) {
  if (state.loading) {
    return (
      <div className="hint">
        <span className="spinner" /> Building structures and checking each one
        against the signal battery's measured record…
      </div>
    );
  }
  if (state.error) return <div className="err">{state.error.message}</div>;
  const d = state.data;
  if (!d) return null;

  const shown = view === "all" ? d.structures
    : d.structures.filter((s) => s.view === view);

  return (
    <>
      <div className="notebox" style={{ borderLeftColor: "var(--accent)" }}>
        {d.headline}
      </div>

      {d.signals.length > 0 && (
        <>
          <h3>Signals firing on {d.symbol}</h3>
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr><th>Signal</th><th>Side</th><th>When</th><th>Verdict</th><th>Record</th></tr>
              </thead>
              <tbody>
                {d.signals.map((s, i) => (
                  <tr key={i}>
                    <td>{s.label}</td>
                    <td className={s.direction > 0 ? "up" : "down"}>
                      {s.direction > 0 ? "long" : "short"}
                    </td>
                    <td className="small nowrap">
                      {s.bars_ago === 0 ? "latest bar" : `${s.bars_ago} bars ago`}
                    </td>
                    <td className="small">{s.verdict ?? "—"}</td>
                    <td className="num small mono">
                      {s.hit_rate !== null
                        ? `${formatPct(s.hit_rate)} vs ${formatPct(s.baseline)} · n=${s.samples}`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3>Structures · {shown.length}</h3>
      {shown.map((s) => (
        <StructureCard key={s.id} s={s} spot={spot} expectedMove={expectedMove} />
      ))}

      <h3>What this does not claim</h3>
      <div className="notebox">
        <ul className="tight">
          {d.not_claimed.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </div>
    </>
  );
}

function StructureCard({
  s, spot, expectedMove,
}: {
  s: Structure; spot: number; expectedMove: number | null;
}) {
  const [open, setOpen] = useState(false);
  const credit = (s.net_debit ?? 0) < 0;
  const vs = s.vs_signal;
  const tone = LIQUIDITY_TONE[s.liquidity] ?? "muted";

  return (
    <div
      style={{
        border: "1px solid var(--border)", borderRadius: 6,
        padding: "9px 11px", marginBottom: 8, background: "var(--bg-sunken)",
      }}
    >
      <div className="row" style={{ alignItems: "baseline", cursor: "pointer" }}
           onClick={() => setOpen((o) => !o)}>
        <strong className="grow">{s.name}</strong>
        <span className={`badge ${tone}`}>{s.liquidity}</span>
        <span className="badge muted">{s.view}</span>
      </div>

      <div className="kv" style={{ marginTop: 8, gridTemplateColumns: "repeat(auto-fill, minmax(124px, 1fr))" }}>
        <Cell
          k="prob. of profit"
          v={formatPct(s.prob_profit)}
          sub="what the market charges"
        />
        <Cell
          k="expected value"
          v={s.expected_value !== null ? `$${formatNum(s.expected_value, 0)}` : "—"}
          sub="under those same prices"
        />
        <Cell
          k={credit ? "credit received" : "debit paid"}
          v={`$${formatNum(Math.abs(s.net_debit ?? 0), 0)}`}
        />
        <Cell
          k="max profit"
          v={s.max_profit !== null ? `$${formatNum(s.max_profit, 0)}` : "unlimited"}
        />
        <Cell
          k="max loss"
          v={s.max_loss !== null ? `$${formatNum(Math.abs(s.max_loss), 0)}` : "unlimited"}
        />
        <Cell
          k="capital"
          v={`$${formatNum(s.capital_required ?? 0, 0)}`}
        />
      </div>

      <div style={{ marginTop: 8 }}>
        <PayoffChart
          legs={s.legs}
          spot={spot}
          expectedMove={expectedMove}
          breakevens={s.breakevens}
        />
        <div className="faint small mono" style={{ marginTop: 3 }}>
          breakeven {s.breakevens.map((b) => formatNum(b)).join(", ") || "—"} ·
          shaded band is the market's 1σ expected move · dashed line is spot
        </div>
      </div>

      {s.warnings.map((w, i) => <div className="warnbox" key={i}>{w}</div>)}

      {vs?.comparable && (
        <div
          className="notebox"
          style={{
            borderLeftColor: vs.verdict === "edge-vs-market"
              ? "var(--tone-good)" : "var(--border-strong)",
          }}
        >
          <strong>
            {vs.verdict === "edge-vs-market" ? "Signal beats the market's price"
              : vs.verdict === "insufficient-sample" ? "Not enough history to compare"
              : "No edge against the market's price"}
          </strong>
          <div style={{ marginTop: 3 }}>{vs.explanation}</div>
        </div>
      )}

      {open && (
        <>
          <div className="hint">{s.rationale}</div>
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr><th>Action</th><th>Type</th><th>Strike</th><th>Price</th><th>Δ</th></tr>
              </thead>
              <tbody>
                {s.legs.map((l, i) => (
                  <tr key={i}>
                    <td className={l.action === "buy" ? "up" : "down"}>{l.action}</td>
                    <td>{l.kind}</td>
                    <td className="num">{formatNum(l.strike, 1)}</td>
                    <td className="num">{formatNum(l.price)}</td>
                    <td className="num">{formatNum(l.delta, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="faint small mono" style={{ marginTop: 4 }}>
            net Δ {formatNum(s.net_delta, 2)} · net Θ ${formatNum(s.net_theta, 2)}/day ·
            net vega ${formatNum(s.net_vega, 2)} per vol point
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────── volatility ───────────────────────────
function Volatility({
  state, symbol,
}: {
  state: ReturnType<typeof useAsync<Awaited<ReturnType<typeof api.vrp>>>>;
  symbol: string;
}) {
  if (state.loading) {
    return <div className="hint"><span className="spinner" /> measuring…</div>;
  }
  if (state.error) return <div className="err">{state.error.message}</div>;
  const d = state.data;
  if (!d) return null;

  if ("error" in d) {
    return (
      <>
        <h3>Variance risk premium · {symbol}</h3>
        <div className="warnbox">{d.error}</div>
        {d.hint && <div className="notebox">{d.hint}</div>}
      </>
    );
  }

  const r = d as VrpResult;
  return (
    <>
      <h3>Variance risk premium · {r.symbol}</h3>
      <div className="kv">
        <Cell
          k="implied exceeded realised"
          v={formatPct(r.hit_rate)}
          sub={`${r.implied_exceeded_realised} of ${r.observations} windows`}
        />
        <Cell
          k="95% interval"
          v={`${formatPct(r.ci_low)} – ${formatPct(r.ci_high)}`}
        />
        <Cell
          k="mean premium"
          v={`${(r.mean_premium * 100).toFixed(2)} pts`}
          sub="implied minus realised"
        />
        <Cell k="mean implied" v={formatPct(r.mean_implied)} />
        <Cell k="mean realised" v={formatPct(r.mean_realised)} />
        <Cell
          k="worst window"
          v={`${(r.worst_premium * 100).toFixed(1)} pts`}
          sub="implied under-priced realised"
        />
      </div>
      <div className="faint small" style={{ marginTop: 6 }}>
        {r.source} · {r.window.start} → {r.window.end} · horizon {r.horizon_days}d
      </div>
      <div className="notebox">
        <ul className="tight">
          {r.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </div>
    </>
  );
}


// ─────────────────────────── expiry picker ───────────────────────────
/**
 * Every listed expiry, behind one button.
 *
 * This was a row of chips. Laid out flat it showed the shape of the surface
 * at a glance -- five weeklies then a jump to three months out -- but a
 * liquid underlying lists thirty of them, and thirty chips on one scrolling
 * line is a wall of numbers. The menu keeps what the chips were carrying:
 * days to expiry on every row, month headers, and standard monthlies marked,
 * so the shape is still readable once it is open.
 */
function ExpiryStrip({
  expiries, selected, onPick,
}: {
  expiries: Expiry[];
  selected: string | null;
  onPick: (e: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const future = useMemo(
    () => expiries.filter((e) => e.days_to_expiry >= 0),
    [expiries],
  );

  // A click anywhere else closes it, as does Escape.
  useEffect(() => {
    if (!open) return;
    const away = (ev: MouseEvent) => {
      if (!boxRef.current?.contains(ev.target as Node)) setOpen(false);
    };
    const esc = (ev: KeyboardEvent) => { if (ev.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  if (future.length === 0) return null;

  const current = future.find((e) => e.expiry === selected) ?? null;
  const label = current
    ? `${MONTHS[Number(current.expiry.slice(5, 7)) - 1]} ${current.expiry.slice(8)}`
    : "~30 days";
  const sub = current
    ? `${current.days_to_expiry}d${current.monthly ? " · monthly" : current.weekly ? " · weekly" : ""}`
    : "nearest listed";

  let lastMonth = "";
  return (
    <div className="expiry-picker" ref={boxRef}>
      <span className="expiry-label">Expiry</span>
      <button
        className={`exp-select ${open ? "open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={current ? `${current.expiry} · ${current.days_to_expiry} days`
          : "Whichever listed expiry is closest to 30 days out"}
      >
        <span className="exp-select-main">{label}</span>
        <span className="exp-select-sub">{sub}</span>
        <i className="exp-caret" />
      </button>

      {open && (
        <div className="exp-menu" role="listbox">
          <button
            className={`exp-row auto ${selected === null ? "on" : ""}`}
            role="option"
            aria-selected={selected === null}
            onClick={() => { onPick(null); setOpen(false); }}
          >
            <span className="exp-row-date">~30 days</span>
            <span className="exp-row-dte">auto</span>
          </button>
          {future.map((e) => {
            const month = e.expiry.slice(0, 7);
            const newMonth = month !== lastMonth;
            lastMonth = month;
            const [, m, d] = e.expiry.split("-");
            return (
              <Fragment key={e.expiry}>
                {newMonth && (
                  <div className="exp-group">
                    {MONTHS[Number(m) - 1]} {e.expiry.slice(0, 4)}
                  </div>
                )}
                <button
                  className={`exp-row ${selected === e.expiry ? "on" : ""} ${
                    e.monthly ? "monthly" : ""
                  }`}
                  role="option"
                  aria-selected={selected === e.expiry}
                  onClick={() => { onPick(e.expiry); setOpen(false); }}
                >
                  <span className="exp-row-date">
                    {MONTHS[Number(m) - 1]} {d}
                  </span>
                  {/* Standard monthlies carry the open interest, so they are
                      named rather than left to a border to imply. */}
                  {e.monthly && <span className="exp-row-tag">monthly</span>}
                  <span className="exp-row-dte">{e.days_to_expiry}d</span>
                </button>
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// ─────────────────────────── list layout ───────────────────────────
/**
 * Calls and puts as separate full-width tables, the way Yahoo lays them out.
 *
 * The split layout puts one strike on one row with calls left and puts right,
 * which is denser and better for comparing the two sides. This one gives each
 * side its own columns, which is easier to scan down a single side -- and is
 * the layout most people have already learned.
 */
function ChainList({
  calls, puts, spot, side, expiry, days, onPickContract,
}: {
  calls: OptionContract[]; puts: OptionContract[]; spot: number; side: string;
  expiry: string; days: number;
  onPickContract?: (c: OptionContract, kind: "call" | "put") => void;
}) {
  return (
    <>
      <h3>
        Chain · <span style={{ color: "var(--text-dim)" }}>{expiry}</span> · {days}d
      </h3>
      {side !== "puts" && (
        <SideTable rows={calls} spot={spot} kind="call" expiry={expiry} days={days}
                   onPick={onPickContract} />
      )}
      {side !== "calls" && (
        <SideTable rows={puts} spot={spot} kind="put" expiry={expiry} days={days}
                   onPick={onPickContract} />
      )}
    </>
  );
}

function SideTable({
  rows, spot, kind, expiry, days, onPick,
}: {
  rows: OptionContract[]; spot: number; kind: "call" | "put";
  expiry: string; days: number;
  onPick?: (c: OptionContract, kind: "call" | "put") => void;
}) {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.strike - b.strike),
    [rows],
  );
  if (sorted.length === 0) return null;

  // Scaled to this chain's own spread of implied vol rather than a fixed
  // range: a quiet name and a name into earnings have nothing in common, and
  // one absolute scale would paint one of them entirely cold.
  const ivs = sorted.map((c) => c.iv).filter((v): v is number => v !== null);
  const ivLo = ivs.length > 0 ? Math.min(...ivs) : 0;
  const ivHi = ivs.length > 0 ? Math.max(...ivs) : 1;
  const ivHeat = (iv: number) =>
    ivHi === ivLo ? 0 : Math.max(0, Math.min(1, (iv - ivLo) / (ivHi - ivLo)));

  return (
    <div style={{ marginTop: 10 }}>
      <div className="side-head">
        <span style={{ color: kind === "call" ? "var(--up)" : "var(--down)" }}>
          {kind === "call" ? "CALLS" : "PUTS"}
        </span>
        <span className="small faint mono">
          {expiry} · {days}d · {sorted.length} strikes
        </span>
      </div>
      <div className="table-scroll" style={{ maxHeight: "42vh", overflowY: "auto" }}>
        <table className="grid chain-list">
          <thead>
            <tr>
              <th>Contract</th>
              <th>Strike</th>
              <th>Last</th>
              <th>Bid</th>
              <th>Ask</th>
              <th>Vol</th>
              <th>OI</th>
              <th>IV</th>
              <th>Δ</th>
              <th>P(ITM)</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((c) => {
              const nearSpot = Math.abs(c.strike - spot) < spot * 0.006;
              return (
                <tr
                  key={c.symbol}
                  // In-the-money rows are tinted, as on every chain people
                  // have used before. It is the one thing the eye uses to
                  // locate itself relative to spot without reading numbers.
                  className={c.in_the_money ? "itm" : undefined}
                  style={{
                    ...(nearSpot ? { outline: "1px solid var(--accent)" } : {}),
                    ...(onPick ? { cursor: "pointer" } : {}),
                  }}
                  onClick={onPick ? () => onPick(c, kind) : undefined}
                >
                  <td className="mono small faint">{c.symbol}</td>
                  <td className="num mono strike">{formatNum(c.strike)}</td>
                  <td className="num">{c.last === null ? "—" : formatNum(c.last)}</td>
                  {/* Bid green, ask red: the two sides of the spread, coloured
                      the way every chain colours them. Not a judgement about
                      the contract -- which side of the book you are reading. */}
                  <td className="num bid">{c.bid === null ? "—" : formatNum(c.bid)}</td>
                  <td className="num ask">{c.ask === null ? "—" : formatNum(c.ask)}</td>
                  <td className={`num ${c.volume ? "" : "faint"}`}>
                    {formatNum(c.volume, 0)}
                  </td>
                  {/* null is "the provider did not report it", which is not
                      zero, and must not be shown as zero. */}
                  <td className={`num ${c.open_interest ? "" : "faint"}`}>
                    {c.open_interest === null ? "—" : formatNum(c.open_interest, 0)}
                  </td>
                  {/* A heat bar behind the figure, scaled across this chain's
                      own IV range, so the skew reads as a shape down the
                      column instead of as numbers to be compared one by one. */}
                  <td
                    className="num iv"
                    style={c.iv === null ? undefined : { "--heat": ivHeat(c.iv) } as React.CSSProperties}
                  >
                    {c.iv === null ? "—" : formatPct(c.iv, 0)}
                  </td>
                  <td className={`num ${c.delta === null ? "" : c.delta >= 0 ? "up" : "down"}`}>
                    {c.delta === null ? "—" : c.delta.toFixed(2)}
                  </td>
                  <td className="num">
                    {c.prob_itm === null ? "—" : formatPct(c.prob_itm, 0)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
